import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ViewChild,
  ElementRef,
  HostListener,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-custom-date-select',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './custom-date-select.component.html',
  styleUrls: ['./custom-date-select.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDateSelectComponent),
      multi: true,
    },
  ],
})
export class CustomDateSelectComponent implements ControlValueAccessor, OnInit {
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() selectionMode: 'year' | 'month' | 'month-year' | 'date' = 'date';
  @Input() placeholder: string = 'Select date';

  @Output() dateChange = new EventEmitter<Date>();

  @ViewChild('dropdownTrigger') dropdownTrigger!: ElementRef;

  selectedDate: Date | null = null;
  selectedYear: number | null = null;
  months = Array.from({ length: 12 }, (_, i) => i);

  showDropdown = false;
  showYearSelection = false;
  showMonthSelection = false;

  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};

  currentYear = new Date().getFullYear();

  // For year pagination grid
  years: number[] = [];
  yearPageStart!: number; // first year in current 16-year page
  readonly yearsPerPage = 16;

  ngOnInit() {
    const minYear = this.minDate?.getFullYear() ?? this.currentYear - 100;
    const maxYear = this.maxDate?.getFullYear() ?? this.currentYear + 10;
    for (let y = minYear; y <= maxYear; y++) {
      this.years.push(y);
    }

    // Initialize pagination start to page containing currentYear or selected year
    const startYearCandidate = this.selectedDate
      ? this.selectedDate.getFullYear()
      : this.currentYear;
    this.yearPageStart =
      startYearCandidate - (startYearCandidate % this.yearsPerPage);
  }

  writeValue(value: Date | null): void {
    this.selectedDate = value;
    if (value) {
      this.selectedYear = value.getFullYear();
      // Reset pagination page start to show selected year
      this.yearPageStart =
        this.selectedYear - (this.selectedYear % this.yearsPerPage);
    }
    this.onChange(this.selectedDate);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  toggleDropdown(event?: MouseEvent) {
    if (event) event.stopPropagation();
    this.showDropdown = !this.showDropdown;

    if (this.selectionMode === 'month-year') {
      this.showYearSelection = true;
      this.showMonthSelection = false;
      this.selectedYear = this.selectedDate
        ? this.selectedDate.getFullYear()
        : null;
      if (this.selectedYear !== null) {
        this.yearPageStart =
          this.selectedYear - (this.selectedYear % this.yearsPerPage);
      }
    } else if (this.selectionMode === 'month') {
      this.showMonthSelection = true;
    } else if (this.selectionMode === 'year') {
      this.showYearSelection = true;
      this.selectedYear = this.selectedDate
        ? this.selectedDate.getFullYear()
        : null;
      if (this.selectedYear !== null) {
        this.yearPageStart =
          this.selectedYear - (this.selectedYear % this.yearsPerPage);
      }
    }
  }

  selectYear(year: number, event?: MouseEvent) {
    if (event) event.stopPropagation();
    if (this.selectionMode === 'month-year') {
      this.selectedYear = year;
      this.showYearSelection = false;
      this.showMonthSelection = true;
    } else {
      const month = this.selectedDate?.getMonth() ?? 0;
      const date = new Date(year, month);
      this.setSelectedDate(date);
    }
  }

  selectMonth(month: number, event?: MouseEvent) {
    if (event) event.stopPropagation();
    const year =
      this.selectionMode === 'month-year'
        ? (this.selectedYear ?? this.currentYear)
        : (this.selectedDate?.getFullYear() ?? this.currentYear);

    const date = new Date(year, month);
    this.setSelectedDate(date);
  }

  onDateChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (value) {
      const date = new Date(value);
      this.setSelectedDate(date);
    }
  }

  setSelectedDate(date: Date) {
    this.selectedDate = date;
    this.onChange(date);
    this.dateChange.emit(date);
    this.showDropdown = false;
    this.showYearSelection = false;
    this.showMonthSelection = false;
  }

  formatDate(date?: Date | null): string {
    if (date && this.maxDate && date > this.maxDate) date = this.maxDate;
    return date ? date.toISOString().split('T')[0] : '';
  }

  formatDisplayDate(): string {
    if (!this.selectedDate) return '';
    if (this.selectionMode === 'year') {
      return this.selectedDate.getFullYear().toString();
    }
    if (this.selectionMode === 'month') {
      return this.getMonthLabel(this.selectedDate.getMonth());
    }
    if (this.selectionMode === 'month-year') {
      return `${this.getMonthLabel(this.selectedDate.getMonth())} ${this.selectedDate.getFullYear()}`;
    }
    // date mode default
    return this.formatDate(this.selectedDate);
  }

  getMonthLabel(month: number): string {
    return new Date(2000, month).toLocaleString('default', { month: 'short' });
  }

  isSelectedMonth(month: number): boolean {
    if (!this.selectedDate) return false;
    return (
      this.selectedDate.getMonth() === month &&
      this.selectedYear === this.selectedDate.getFullYear()
    );
  }

  isSelectedYear(year: number): boolean {
    if (!this.selectedDate) return false;
    return this.selectedDate.getFullYear() === year;
  }

  getYearsForPage(): number[] {
    return this.years.filter(
      (y) =>
        y >= this.yearPageStart && y < this.yearPageStart + this.yearsPerPage,
    );
  }

  changeYearPage(deltaPages: number) {
    const newStart = this.yearPageStart + deltaPages * this.yearsPerPage;

    // Clamp newStart within available years range
    if (newStart < this.years[0]) {
      this.yearPageStart = this.years[0];
    } else if (newStart > this.years[this.years.length - 1]) {
      this.yearPageStart =
        this.years[this.years.length - 1] - this.yearsPerPage + 1;
    } else {
      this.yearPageStart = newStart;
    }
  }

  isYearDisabled(year: number): boolean {
    if (this.minDate && year < this.minDate.getFullYear()) return true;
    if (this.maxDate && year > this.maxDate.getFullYear()) return true;
    return false;
  }

  isMonthDisabled(month: number): boolean {
    if (
      this.minDate &&
      this.selectedYear === this.minDate.getFullYear() &&
      month < this.minDate.getMonth()
    )
      return true;
    if (
      this.maxDate &&
      this.selectedYear === this.maxDate.getFullYear() &&
      month > this.maxDate.getMonth()
    )
      return true;
    return false;
  }

  isPrevPageDisabled(): boolean {
    return this.yearPageStart <= this.years[0];
  }

  isNextPageDisabled(): boolean {
    return (
      this.yearPageStart + this.yearsPerPage > this.years[this.years.length - 1]
    );
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: MouseEvent) {
    if (!this.dropdownTrigger?.nativeElement.contains(event.target)) {
      this.showDropdown = false;
      this.showYearSelection = false;
      this.showMonthSelection = false;
    }
  }
}
